import * as React from 'react';

import classnames from 'classnames';
import * as ramda from 'ramda';

// @ts-expect-error - imported from .js/.jsx file
import {CogIcon} from '../icon/svg/cog-icon';
import {StackLegacy} from '../layout/stack/stack-legacy';
import {ListView} from '../list-view/list-view';
import {SearchBar} from '../search-bar/search-bar';

import {SelectableListRow} from './selectable-list-row';
import type {Model} from './selectable-list-row';

import styles from './selectable-list.module.css';

export type FooterButtonProps = {
    label: string;
    onClick: () => void;
    icon?: React.ComponentType<any>;
};

type Props<T> = {
    /** An array of models (objects with id, name, and type) to show in the list */
    collection: T[];
    /** If true, it will be impossible to deselect all items */
    alwaysHasValue?: boolean;
    /** If true, more than one value is possible */
    isMultiSelect?: boolean;
    hasSearch?: boolean;
    /** If provided, this will show when no other results are available */
    emptyStateComponent?: React.ReactNode;
    /** The currently-selected model(s) */
    value: string | string[] | null | undefined;
    onSelect: (selected?: any) => void;
    shouldAllowSelectNone?: boolean;
    isNoneSelected: boolean;
    onSelectNone: (selectNone: boolean) => void;
    /** Dumb, hopefully temp hack prop */
    isNewStyle?: boolean;
    /** Should the search bar be auto focused, as in a popover? */
    autoFocusSearch?: boolean;
    paginationProps?: {
        hasNextPage: boolean;
        isLoading: boolean;
        onFetchMoreRows: () => void;
    };
    sortInitialValueToTop?: boolean;
    placeholder?: string;
    footerButton?: FooterButtonProps;
};

type State<T> = {
    searchQuery: string;
    /** This is only set in the constructor, so we can bubble the pre-set values to the top of the list */
    initialValues: string[];
    sortedCollection: T[];
    selectNone: boolean;
};

const LOAD_MORE_PIXEL_THRESHOLD = 250;

/**
This component can be used to render a list of users and/or teams, and can be configured
to support single or multi-select, along with a few other options.

By default a search field is provided at the top to narrow down the list, but this can be disabled if desired.
 */
class SelectableList<T extends Model> extends React.Component<Props<T>, State<T>> {
    listRef: HTMLElement | null | undefined;
    state: State<T>;

    static defaultProps = {
        alwaysHasValue: false,
        isMultiSelect: false,
        hasSearch: true,
        shouldAllowSelectNone: false,
        isNoneSelected: false,
        onSelectNone: () => {},
        sortInitialValueToTop: true,
        placeholder: 'Search…',
    };

    constructor(props: Props<T>) {
        super(props);
        // @ts-expect-error
        let initialValues = [];
        if (props.value) {
            if (Array.isArray(props.value)) {
                initialValues = props.value;
            } else {
                initialValues = [props.value];
            }
        }
        const sortedCollection = moveInitialValuesToTop(
            props.collection,
            // @ts-expect-error
            initialValues,
            props.sortInitialValueToTop
        );

        this.state = {
            searchQuery: '',
            // @ts-expect-error
            initialValues,
            sortedCollection,
            selectNone: props.isNoneSelected,
        };
    }

    componentDidMount() {
        if (this.listRef) {
            this.listRef.addEventListener('scroll', this.handleScroll);
        }
    }

    componentWillUnmount() {
        if (this.listRef) {
            this.listRef.removeEventListener('scroll', this.handleScroll);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props<T>) {
        if (nextProps.collection !== this.props.collection) {
            const sortedCollection = moveInitialValuesToTop(
                nextProps.collection,
                // We can safely disable this eslint rule because initialValues state never changes

                this.state.initialValues,
                this.props.sortInitialValueToTop
            );

            this.setState({sortedCollection});
        }
    }

    render() {
        const filteredCollection = this.state.sortedCollection.filter(this.filterForSearch);

        // @ts-expect-error
        let values = [];
        if (this.props.value) {
            values = Array.isArray(this.props.value) ? this.props.value : [this.props.value];
        }

        const EmptyStateComponent = (
            <ul className={styles['empty-container']}>
                <li>{this.props.emptyStateComponent}</li>
            </ul>
        );

        const Icon = (this.props.footerButton && this.props.footerButton.icon) || CogIcon;

        return (
            <div className={this.props.isNewStyle ? styles['container-new'] : styles.container}>
                {this.props.hasSearch ? (
                    <div className={styles.search}>
                        <SearchBar
                            hasBorder={this.props.isNewStyle}
                            value={this.state.searchQuery}
                            autoFocus={this.props.autoFocusSearch}
                            onChange={this.handleTextChange}
                            placeholder={this.props.placeholder}
                        />
                    </div>
                ) : null}
                {this.props.shouldAllowSelectNone ? (
                    <div className={classnames(styles['select-none'], styles['list-view'])}>
                        <SelectableListRow
                            key='select-none'
                            hasCheckboxes={true}
                            isBorderless={true}
                            isChecked={this.state.selectNone}
                            item={{
                                id: 'none',
                                name: 'None',
                                type: 'fake',
                            }}
                            onSelect={this.handleSelectNone}
                        />
                    </div>
                ) : undefined}
                <div
                    ref={(c) => (this.listRef = c)}
                    className={
                        this.props.footerButton
                            ? styles['list-view-with-footer']
                            : styles['list-view']
                    }
                >
                    <ListView
                        // @ts-expect-error
                        renderRow={(item: T) => this.renderRow(item, values)}
                        collection={filteredCollection}
                        specialStates={[
                            {
                                shouldRender: () =>
                                    Boolean(
                                        filteredCollection.length === 0 &&
                                            this.props.emptyStateComponent
                                    ),
                                component: EmptyStateComponent,
                            },
                            {
                                shouldRender: () =>
                                    Boolean(
                                        filteredCollection.length === 0 && this.state.searchQuery
                                    ),
                                component: (
                                    <ul className={styles['empty-container']}>
                                        <li>{`No matches for "${this.state.searchQuery}"`}</li>
                                    </ul>
                                ),
                            },
                        ]}
                    />
                </div>
                {this.props.footerButton && (
                    <div onClick={this.props.footerButton.onClick} className={styles.footer}>
                        <StackLegacy spacing={8} isHorizontal={true} alignCenter={true}>
                            <Icon size={13} alignCenter={true} />
                            <div>{this.props.footerButton.label}</div>
                        </StackLegacy>
                    </div>
                )}
            </div>
        );
    }

    handleTextChange = (val: string) => {
        this.setState({searchQuery: val});
    };

    renderRow = (item: Model, values: string[]) => {
        const isChecked = values.includes(item.id);

        return (
            <SelectableListRow
                key={item.id}
                hasCheckboxes={this.props.isMultiSelect}
                isBorderless={this.props.isNewStyle}
                isChecked={isChecked}
                item={item}
                icon={item.icon}
                isDisabled={item.isDisabled || this.state.selectNone}
                tooltipText={item.tooltipText}
                onSelect={this.handleSelect}
            />
        );
    };

    handleSelect = (item: Model, isCurrentlySelected: boolean) => {
        // @ts-expect-error
        let value = [];
        if (this.props.value) {
            value = Array.isArray(this.props.value) ? this.props.value : [this.props.value];
        }
        const {alwaysHasValue, isMultiSelect} = this.props;

        let returnValue = null;
        if (!isMultiSelect) {
            // If the item isn't currently selected or we always have a value
            // we'll return the selected item
            if (!isCurrentlySelected || alwaysHasValue) {
                returnValue = item.id;
            }
        } else if (value.length === 1 && alwaysHasValue && isCurrentlySelected) {
            returnValue = [item.id];
        } else {
            // @ts-expect-error
            returnValue = [...value];
            returnValue = isCurrentlySelected
                ? returnValue.filter((selectedModelId) => selectedModelId !== item.id)
                : returnValue.concat([item.id]);
        }

        this.props.onSelect(returnValue);
    };

    handleSelectNone = (item: Model, isCurrentlySelected: boolean) => {
        const shouldSelectNone = !isCurrentlySelected;

        this.setState({selectNone: shouldSelectNone});
        this.props.onSelectNone(shouldSelectNone);
    };

    filterForSearch = (item: Model) => {
        if (!this.state.searchQuery || !item.name) return true;

        return !(item.name.toLowerCase().indexOf(this.state.searchQuery.toLowerCase()) === -1);
    };

    handleScroll = (e: Event) => {
        // @ts-expect-error
        const {scrollHeight, offsetHeight, scrollTop} = e.target;

        if (
            scrollHeight - offsetHeight < scrollTop + LOAD_MORE_PIXEL_THRESHOLD &&
            this.props.paginationProps &&
            this.props.paginationProps.hasNextPage &&
            !this.props.paginationProps.isLoading
        ) {
            this.props.paginationProps.onFetchMoreRows();
        }
    };
}

type SingleProps<T> = {
    /** An array of models (objects with id, name, and type) to show in the list */
    collection: T[];
    /** If true, it will be impossible to deselect all items */
    alwaysHasValue?: boolean;
    hasSearch?: boolean;
    /** If provided, this will show when no other results are available */
    emptyStateComponent?: React.ReactNode;
    /** The currently-selected model(s) */
    value: string | null | undefined;
    // TODO: Remove nullable type when alwaysHasValue is true (disjoint union types with Flow 53+)
    onSelect: (selected?: string | null | undefined) => void;
    footerButton?: FooterButtonProps;
    autoFocusSearch?: boolean;
    isNewStyle?: boolean;
};

/**
This component can be used to render a list of users and/or teams (or other models), which can be
clicked on to be selected.  This is a single-select, so each click will move the selection.

Initial value is sorted to the top, disabled items are sorted to the bottom.

By default a search field is provided at the top to narrow down the list, but this can be disabled if desired.
 */
// @ts-expect-error
export class SelectableListSingle<T extends $Supertype<Model>> extends React.Component<
    SingleProps<T>,
    undefined
> {
    render() {
        return <SelectableList {...this.props} isMultiSelect={false} />;
    }
}

type MultipleProps<T> = {
    /** An array of models (objects with id, name, and type) to show in the list */
    collection: T[];
    /** If true, it will be impossible to deselect all items */
    alwaysHasValue?: boolean;
    hasSearch?: boolean;
    /** If provided, this will show when no other results are available */
    emptyStateComponent?: React.ReactNode;
    /** The currently-selected model(s) */
    value: string[] | null | undefined;
    onSelect: (selected: string[]) => void;
    paginationProps?: {
        hasNextPage: boolean;
        isLoading: boolean;
        onFetchMoreRows: () => void;
    };
    footerButton?: FooterButtonProps;
    shouldAllowSelectNone?: boolean;
    isNoneSelected?: boolean;
    onSelectNone?: (selectNone: boolean) => void;
    autoFocusSearch?: boolean;
    isNewStyle?: boolean;
};

/**
This component can be used to render a list of users and/or teams (or other models), which can be
clicked on to be selected.  This is a multi-select, so each click will add or remove to the list of
selected items.

Initial values are sorted to the top, disabled items are sorted to the bottom.

By default a search field is provided at the top to narrow down the list, but this can be disabled if desired.
*/
// @ts-expect-error
export class SelectableListMultiple<T extends $Supertype<Model>> extends React.Component<
    MultipleProps<T>,
    undefined
> {
    render() {
        return <SelectableList {...this.props} isMultiSelect={true} />;
    }
}

/*
 * This will pull any models having an id provided in the `initialValues` argument
 * up to the top of the list, without changing the overall sort order of the list
 * in any other way.
 */
export function moveInitialValuesToTop<T extends Model>(
    collection: T[],
    initialValues: string[],
    shouldSort = true
): T[] {
    if (!shouldSort) {
        return collection;
    }

    const initialCollection = collection.filter((model) => initialValues.includes(model.id));
    const remainingCollection = ramda.without(initialCollection, collection);

    return initialCollection.concat(remainingCollection);
}
